import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fixarDiscreto } from '../../web/src/nucleo/discreto';
import { nomePessoa } from '../../web/src/nucleo/formato';
import { pget } from './api';

/** Portal do Cliente (Onda 1): a sessão do app (`GET /api/portal/v1/sessao`). */
export interface SessaoPortal {
  conta: { nome: string; email: string; idioma: 'pt' | 'en' | 'es' } | null;
  empresa: { nome: string; escritorio: string };
  papel: 'dono' | 'financeiro' | 'dp' | 'fiscal' | 'societario' | 'colaborador';
  areas: string[];
  somente_leitura: boolean;
  termo_pendente: boolean;
  sandbox: boolean;
  confiavel: boolean;
  /** Pacote 2: `assinaturas` com o módulo Documentos e assinatura ligado no escritório (o cliente assina em Documentos). */
  secoes: { financeiro: boolean; documentos: boolean; mensagens: boolean; relatorios?: boolean; assinaturas?: boolean };
  push: { chave_publica: string | null };
  /** Trajetórias (item 5): modo saída (último contrato encerrado) até a data; nulo no acesso normal. */
  saida?: { ate: string } | null;
  via: 'cookie' | 'bearer';
  /** Review r23 visual (M4): no "ver como", o modo discreto de quem abriu (gravado no servidor); nulo na sessão do cliente. */
  discreto?: { ativo: boolean; nomes: boolean } | null;
}

export const CHAVE_SESSAO = ['portal', 'sessao'];

/**
 * Review r23 visual (M4): a sessão "ver como" impõe o modo discreto de quem abriu, antes de qualquer tela renderizar (os
 * formatadores do núcleo, `dinheiro`, `nomePessoa`, `textoLivre`, consultam o modo). Só na memória desta aba: a origem do
 * portal não guarda nada. A sessão do cliente não mexe no modo.
 */
export function aplicarDiscretoDaSessao(s: Pick<SessaoPortal, 'somente_leitura' | 'discreto'>) {
  if (s.somente_leitura) fixarDiscreto(s.discreto ?? { ativo: false, nomes: false });
}

export function useSessaoQuery() {
  return useQuery({
    queryKey: CHAVE_SESSAO,
    queryFn: async () => {
      const s = await pget<SessaoPortal>('/sessao');
      aplicarDiscretoDaSessao(s);
      return s;
    },
    staleTime: 60_000,
    retry: false,
  });
}

const Contexto = createContext<SessaoPortal | null>(null);

export function ProvedorSessao({ dados, children }: { dados: SessaoPortal; children: ReactNode }) {
  return <Contexto.Provider value={dados}>{children}</Contexto.Provider>;
}

export function useSessao(): SessaoPortal {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSessao fora do ProvedorSessao');
  return s;
}

/** Nome da empresa do cliente na tela: iniciais com a opção de nomes do modo discreto (§6, `nomePessoa`). */
export const nomeEmpresa = (s: SessaoPortal) => nomePessoa(s.empresa.nome);

/** Primeiro nome da conta (saudação). */
export const primeiroNome = (s: SessaoPortal) => (s.conta?.nome ?? '').trim().split(/\s+/)[0] ?? '';
